import { MiraSectionDocument, MiraTrendGrain, MiraTrendPoint } from "@/data/models/Mira";
import { firstYearWithCases } from "@/utils/MiraDataQuality";
import { MiraViewContext } from "@/utils/MiraDataQualityView";

/**
 * The Verlauf (timeline) card's view model: a metric's published trend at one grain, turned into
 * points the chart draws, with labels in the locale, formatted values in the figure's own unit, gaps
 * for the periods that had nothing, and the percentiles where they are present.
 *
 * Pure, like the other Mira view models: it takes the document's `trends` entry, a grain and the
 * screen's context, and never mounts anything or reads a repository. The formatter is supplied by the
 * screen (`dryingTrendFormatter`, `businessTrendFormatter`, …) so a point reads in the same unit as its
 * tile: days, a share, a rate, a count.
 */

export type { MiraTrendGrain } from "@/data/models/Mira";

/**
 * The figures that add up over a period (Projekte, Stromverbrauch total, Gerätetage, Betriebsstunden).
 * In the Verlauf card a total draws as bars, because a sum per month is compared bar to bar, not read as
 * a level; every other figure keeps its line (Hans, 2026-10-05). A total carries no distribution, so it
 * never gets the median line or the percentile band.
 */
export const MIRA_TOTAL_METRICS = ["MIRA-PRJ-001", "MIRA-DEV-004", "MIRA-DEV-001", "MIRA-DEV-002"];

/** Whether a figure is a total: drawn as bars, with no median line and no band. */
export function isTotalMetric(metricId: string): boolean {
  return MIRA_TOTAL_METRICS.includes(metricId);
}

/** The three grains in display order, for the Monat / Quartal / Jahr switch. */
export const MIRA_TREND_GRAINS: MiraTrendGrain[] = ["month", "quarter", "year"];

/**
 * The grains a comparison offers, totals and averages alike: five bars per month over 24 months is too
 * dense to read, and an average per member per month rests on too few cases (Taimoor, 2026-10-13).
 */
export const MIRA_COMPARISON_GRAINS: MiraTrendGrain[] = ["quarter", "year"];

/**
 * The grain a card draws: the shared choice where the card offers it, else the card's first offered
 * grain. Display only, so a comparison showing Quartal for a chosen Monat hands Monat back to the
 * Verlauf card once the comparison is closed.
 */
export function offeredGrain(chosen: MiraTrendGrain, offered: MiraTrendGrain[]): MiraTrendGrain {
  return offered.includes(chosen) ? chosen : offered[0];
}

/** A period's bounds, as the document publishes them: `from` inclusive, `toExclusive` exclusive. */
export interface MiraTrendRange {
  from: string;
  toExclusive: string;
}

/** One point on the timeline, ready to draw. */
export interface MiraTrendPointView {
  key: string;
  /** The point's own period bounds, so the chosen period can be marked on the timeline. */
  from: string;
  toExclusive: string;
  /** "Okt 25" (month), "Q3 2025" (quarter), "2025" or "2026 (bis heute)" (year). */
  label: string;
  /** `null` is a gap: the period had nothing, or too few rows (`belowFloor`). */
  value: number | null;
  /** The value in the figure's unit, or `null` for a gap. */
  valueText: string | null;
  basis: number;
  median: number | null;
  p15: number | null;
  p85: number | null;
  /** The current year to date: drawn hollow, not a full period. */
  partial: boolean;
  belowFloor: boolean;
  /** "zu wenige" where the point is below the figure's floor; `null` otherwise. */
  note: string | null;
}

/** A metric's timeline at one grain. */
export interface MiraTrendView {
  points: MiraTrendPointView[];
  /** Whether any point carries a value: an all-gap grain shows the empty note instead of a chart. */
  hasData: boolean;
}

/** "2026-09" to "Okt 25", in the active locale (German where none is given). */
function monthLabel(key: string, ctx: MiraViewContext): string {
  const [year, month] = key.split("-");
  const monthIndex = Number(month) - 1;
  if (!year || Number.isNaN(monthIndex)) {
    return key;
  }

  const date = new Date(Date.UTC(2000, monthIndex, 1));
  // A fixed day of the month in UTC, so the month name never slips across a time zone boundary.
  const name = new Intl.DateTimeFormat(ctx.locale ?? "de", { month: "short", timeZone: "UTC" })
    .format(date)
    .replace(/\.$/, "");
  return `${name} ${year.slice(2)}`;
}

/** "2026-Q3" to "Q3 2026". */
function quarterLabel(key: string): string {
  const [year, quarter] = key.split("-");
  return quarter ? `${quarter} ${year}` : key;
}

function pointLabel(point: MiraTrendPoint, grain: MiraTrendGrain, ctx: MiraViewContext): string {
  if (grain === "month") {
    return monthLabel(point.key, ctx);
  }
  if (grain === "quarter") {
    return quarterLabel(point.key);
  }
  return point.partial ? ctx.t("mira.trend.partialYear", [point.key]) : point.key;
}

/**
 * A metric's raw points at one grain, the year view starting at the document's first year with cases
 * ({@link firstYearWithCases}). Every builder reads its points through here, so a builder that pairs its
 * points with the raw ones by index (the stacked view) can never drift from {@link buildTrend}.
 */
export function trendSeriesPoints(
  trends: MiraSectionDocument["trends"] | undefined,
  metricId: string,
  grain: MiraTrendGrain
): MiraTrendPoint[] {
  const raw = trends?.[metricId]?.[grain] ?? [];
  const firstYear = grain === "year" ? firstYearWithCases(trends) : null;
  return firstYear === null ? raw : raw.filter((point) => point.key >= firstYear);
}

/**
 * The points of a metric's timeline at one grain. A point below its floor, or with no value, becomes a
 * gap (`value: null`), so the line breaks there rather than dropping to zero; a below-floor point also
 * carries the "zu wenige" note.
 */
export function buildTrend(
  trends: MiraSectionDocument["trends"] | undefined,
  metricId: string,
  grain: MiraTrendGrain,
  formatValue: (value: number) => string,
  ctx: MiraViewContext
): MiraTrendView {
  const raw = trendSeriesPoints(trends, metricId, grain);

  const points: MiraTrendPointView[] = raw.map((point) => {
    const value = point.value;
    const hasValue = value !== null && value !== undefined && point.belowFloor !== true;

    return {
      key: point.key,
      from: point.from,
      toExclusive: point.toExclusive,
      label: pointLabel(point, grain, ctx),
      value: hasValue ? (value as number) : null,
      valueText: hasValue ? formatValue(value as number) : null,
      basis: point.basis ?? 0,
      median: point.median ?? null,
      p15: point.p15 ?? null,
      p85: point.p85 ?? null,
      partial: point.partial === true,
      belowFloor: point.belowFloor === true,
      note: point.belowFloor === true ? ctx.t("mira.trend.tooFew") : null,
    };
  });

  return { points, hasData: points.some((point) => point.value !== null) };
}

/**
 * The first and last point that lie wholly inside a period, or `null` when none does. A point counts
 * only when its whole span is inside, so a year never marks as "inside" the last 12 months.
 */
export function pointsInRange(
  points: MiraTrendPointView[],
  range: MiraTrendRange | null
): { first: number; last: number } | null {
  if (!range) {
    return null;
  }

  const inside = points
    .map((point, index) => (point.from >= range.from && point.toExclusive <= range.toExclusive ? index : -1))
    .filter((index) => index >= 0);
  return inside.length ? { first: inside[0], last: inside[inside.length - 1] } : null;
}

/**
 * The grain to show for a chosen period: the current one when it has a point inside the period, else the
 * finest view that has one (month, quarter, year; a year before the 24 months, such as 2023, is only in
 * the year view), else the current one unchanged.
 */
export function grainForRange(
  trends: MiraSectionDocument["trends"] | undefined,
  metricId: string,
  current: MiraTrendGrain,
  range: MiraTrendRange | null,
  ctx: MiraViewContext
): MiraTrendGrain {
  const identity = (value: number) => String(value);
  const hasInside = (grain: MiraTrendGrain) =>
    pointsInRange(buildTrend(trends, metricId, grain, identity, ctx).points, range) !== null;

  if (!range || hasInside(current)) {
    return current;
  }
  return MIRA_TREND_GRAINS.find((grain) => hasInside(grain)) ?? current;
}

/**
 * The median a point draws on the thin median line: the published median where the point has a value,
 * else a gap. A true gap (`value === null`) stays a gap, so the median line breaks over it exactly as
 * the mean line does.
 */
export function trendMedianValue(point: MiraTrendPointView): number | null {
  return point.value === null ? null : point.median;
}

/** Whether any point carries a median, so the thin median line has something to draw at this grain. */
export function trendHasMedian(points: MiraTrendPointView[]): boolean {
  return points.some((point) => trendMedianValue(point) !== null);
}

/**
 * Whether a document carries a usable timeline for a metric: a series with at least one real point at
 * some grain. Older documents carry no `trends` at all, and a figure with no series has no entry; both
 * hide the Verlauf card.
 */
export function hasTrend(trends: MiraSectionDocument["trends"] | undefined, metricId: string): boolean {
  const series = trends?.[metricId];
  if (!series) {
    return false;
  }

  return MIRA_TREND_GRAINS.some((grain) =>
    (series[grain] ?? []).some((point) => point.value !== null && point.value !== undefined && point.belowFloor !== true)
  );
}
